import XDate from 'xdate'
import type { AvailabilitySlot } from '../../_types/availability'
import { getIsoDayInTimezone } from '../../_utilities/formatDateTime'
import type { CalendarDayCell } from './types'

const MS_PER_DAY = 86_400_000

/**
 * Returns the ISO date (YYYY-MM-DD) of "today" in the given timezone. Used as
 * the lower bound on the calendar and to highlight today's cell.
 *
 * @param {string} timezone - IANA timezone.
 * @returns {string} ISO date for today in that timezone.
 */
const getTodayIsoInTimezone = (timezone: string): string =>
  getIsoDayInTimezone(Date.now(), timezone)

/**
 * Builds a 6-week x 7-day grid of `CalendarDayCell`s for a given month. The
 * grid always starts on Sunday and may include cells before/after the target
 * month so the grid is rectangular.
 *
 * @param {number} year - Four-digit year.
 * @param {number} monthIndex - 0-indexed month (0 = January).
 * @param {Set<string>} datesWithSlots - ISO dates that have at least one slot.
 * @param {string} todayIso - ISO date for today in the active timezone.
 * @returns {CalendarDayCell[]} 42 cells (6 weeks).
 */
const buildMonthGrid = (
  year: number,
  monthIndex: number,
  datesWithSlots: Set<string>,
  todayIso: string,
): CalendarDayCell[] => {
  const firstOfMonth = new XDate(year, monthIndex, 1)
  const gridStart = firstOfMonth.clone().addDays(-firstOfMonth.getDay())

  const cells: CalendarDayCell[] = []
  for (let dayIndex = 0; dayIndex < 42; dayIndex += 1) {
    const day = gridStart.clone().addDays(dayIndex)
    const isoDate = day.toString('yyyy-MM-dd')
    cells.push({
      isoDate,
      dayOfMonth: day.getDate(),
      isOutsideMonth: day.getMonth() !== monthIndex,
      isToday: isoDate === todayIso,
      hasSlots: datesWithSlots.has(isoDate),
    })
  }
  return cells
}

/**
 * Groups availability slots into a map keyed by ISO date (in the active
 * timezone) → array of slots that start on that date.
 *
 * @param {AvailabilitySlot[]} slots - The flat slot list from the server.
 * @param {string} timezone - IANA timezone for date evaluation.
 * @returns {Record<string, AvailabilitySlot[]>} Slots grouped by ISO date.
 */
const groupSlotsByDate = (
  slots: AvailabilitySlot[],
  timezone: string,
): Record<string, AvailabilitySlot[]> => {
  const groups: Record<string, AvailabilitySlot[]> = {}
  for (const slot of slots) {
    const isoDate = getIsoDayInTimezone(slot.startTime, timezone)
    if (!groups[isoDate]) {
      groups[isoDate] = []
    }
    groups[isoDate].push(slot)
  }
  return groups
}

/**
 * Returns a start/end ISO date pair covering the visible month plus one buffer
 * week on each side. Used as the `startDate`/`endDate` window for the
 * `getavailability` backend call.
 *
 * @param {number} year - Visible month's year.
 * @param {number} monthIndex - Visible month's 0-indexed month.
 * @returns {{ startIso: string, endIso: string }} ISO date range.
 */
const getVisibleRangeIso = (
  year: number,
  monthIndex: number,
): { startIso: string; endIso: string } => {
  const firstOfMonth = new XDate(year, monthIndex, 1)
  const lastOfMonth = firstOfMonth.clone().addMonths(1).addDays(-1)
  const start = firstOfMonth.clone().addDays(-7)
  const end = lastOfMonth.clone().addDays(7)
  return {
    startIso: start.toString('yyyy-MM-dd'),
    endIso: end.toString('yyyy-MM-dd'),
  }
}

/**
 * Returns the month name + year for the calendar header (e.g. `May 2026`).
 *
 * @param {number} year - Four-digit year.
 * @param {number} monthIndex - 0-indexed month.
 * @returns {string} Header label.
 */
const formatMonthHeader = (year: number, monthIndex: number): string =>
  new XDate(year, monthIndex, 1).toString('MMMM yyyy')

/**
 * Formats a duration in minutes as a short human label suitable for the
 * duration chips. Examples:
 * - 15 → `15 min`
 * - 60 → `1 hour`
 * - 90 → `1.5 hours`
 * - 120 → `2 hours`
 *
 * @param {number} minutes - Duration in minutes.
 * @returns {string} Short label.
 */
const formatDurationLabel = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`
  }
  const hours = minutes / 60
  if (hours === 1) {
    return '1 hour'
  }
  if (hours % 1 === 0) {
    return `${hours} hours`
  }
  return `${hours.toFixed(1)} hours`
}

export {
  MS_PER_DAY,
  getTodayIsoInTimezone,
  buildMonthGrid,
  groupSlotsByDate,
  getVisibleRangeIso,
  formatMonthHeader,
  formatDurationLabel,
}
